<template>
  <v-card flat>
    <v-toolbar flat color="fill">
      <ToggleExpanded :refs="$refs"></ToggleExpanded>
      <v-divider vertical inset class="mx-4" />
      <span class="text-center mr-3">
        Vendor Totals by Store
      </span>
      <v-col cols="3">
        <v-autocomplete
          v-model="selectedStore"
          label="Store Filter"
          :items="stores"
          background-color="input"
          hide-details
          clearable
          outlined
          dense>
        </v-autocomplete>
      </v-col>
      <v-col cols="3">
        <v-autocomplete
          v-model="selectedVendor"
          label="Vendor Filter"
          :items="vendors"
          background-color="input"
          hide-details
          clearable
          outlined
          dense>
        </v-autocomplete>
      </v-col>
      <v-spacer></v-spacer>
      <v-col cols="3" class="pl-0">
        <v-text-field
          :label="filter ? 'Press Enter to Search' : 'Search Recap'"
          prepend-inner-icon="mdi-magnify"
          hide-details
          outlined
          dense
          background-color="input"
          v-model="filter"
          clearable>
        </v-text-field>
      </v-col>
    </v-toolbar>
    <v-divider />
    <v-data-table
      :height="tableSize"
      :loading="loading"
      :headers="headers"
      :items.sync="currentRecaps"
      color="#fff"
      loading-text="Loading... Please wait"
      fixed-header
      :items-per-page="pageSize"
      :group-by="groupKey"
      dense
      :search="filter"
      hide-default-footer>
      <template v-slot:[`group.header`]="{ group, headers, toggle, isOpen }">
        <td :colspan="headers.length">
          <v-row dense>
            <v-btn @click="toggle" small icon :ref="group" :data-open="isOpen">
              <v-icon>{{ isOpen ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </v-btn>
            <v-col>{{ group }}</v-col>
            <v-col cols="auto" class="ml-auto mr-6">
              <span style="display: inline-block">
                Total Allowance: ${{ sumAllowance(groupKey, group, currentRecaps) }}
              </span>
            </v-col>
          </v-row>
        </td>
      </template>

      <template #[`item.allowance_earned`]="{item}">
        <span>${{ formatCurrencyDisplay(item.allowance_earned) }}</span>
      </template>
    </v-data-table>

    <!-- Add BatchTableFooter -->
    <BatchTableFooter
      :currentPage="page"
      :itemsPerPage="pageSize"
      :hasNextPage="!disableNext"
      :itemsPerPageOptions="pageSizes"
      @previous-page="previous"
      @next-page="next"
      @update:items-per-page="updatePageSize"
    />
  </v-card>
</template>
<script>
// Components
import ToggleExpanded from '@/components/ToggleExpanded.vue';
import BatchTableFooter from '@/components/BatchTables/SharedElements/BatchTableFooter.vue';
// Mixins
import { fullWidth } from '@/mixins/fullWidth'
import { format } from '@/mixins/format'
import { displayAlert } from '@/mixins/displayAlert'
import { utils } from '@/mixins/utils'
export default {
  name: 'vendor-table',
  data () {
    return {
      filter: '',
      loading: false,
      search: '',
      vendorRecaps: [],
      groupKey: 'store_name_num',
      selectedVendor: '',
      selectedStore: '',
      page: 1,
      pageSize: 50,
      pageSizes: [50, 100, 200],
    };
  },
  components: { ToggleExpanded, BatchTableFooter },
  mixins: [fullWidth, format, displayAlert, utils],
  props: ['panel_height', 'selectedBatch'],
  watch: {
    filter: {
      handler () {
        if (this.filter === null) {
          this.filter = ''
        }
      }
    },
    panel_height: {
      handler (value) {
        this.nonTableHeight = value
      } 
    },
    pageSize: {
      handler() {
        if (this.page > 1) this.page = 1;
        this.getVendorRecaps();
      },
    },
  },
  created () {
    this.nonTableHeight = this.panel_height
    this.getVendorRecaps()
  },
  computed: {
    offset() {
      return (this.page - 1) * this.pageSize;
    },
    disableNext() {
      return this.currentRecaps.length < this.pageSize;
    },
    currentRecaps () {
      let recaps = this.vendorRecaps
      if (this.selectedVendor) {
        recaps = recaps.filter(r => r?.display_name === this.selectedVendor)
      }
      if (this.selectedStore) {
        recaps = recaps.filter(r => r?.store_name_num === this.selectedStore)
      }
      return recaps
    },
    vendors () {
      return (this.vendorRecaps.length > 0)
        ? this.getUniqueByKey(this.vendorRecaps, 'display_name')
        : []
    },
    stores () {
      return (this.vendorRecaps.length > 0)
        ? this.getUniqueByKey(this.vendorRecaps, 'store_name_num')
        : []
    },
    headers() {
      return [
        { sortable: false, filterable: false },
        { text: 'Store / Vendor', value: 'store_name_num', class: 'black--text' },
        { text: 'Store / Vendor', value: 'display_name', class: 'black--text' },
        { text: 'Department', value: 'department', class: 'black--text' },
        { text: 'Vendor Allowance', value: 'allowance_earned', class: 'black--text' },
      ]
    }
  },
  methods: {
    async getVendorRecaps() {
      const { id } = this.selectedBatch;
      this.loading = true;
      try {
        const response = await this.$BillingBatch.getVendorRecaps(id, this.pageSize, this.offset);
        if (response?.data) {
          const formattedRecaps = this.formatRecaps(response.data);
          this.vendorRecaps = formattedRecaps;
        }
      } catch (err) {
        this.handleError(err);
      } finally {
        this.loading = false;
      }
    },
    formatRecaps(results) {
      return results.map(result => {
        result.display_name = `${result.vendor_id} ${result.vendor_name}`
        return result
      })
    },
    updatePageSize(size) {
      this.pageSize = size;
      this.page = 1;
      this.getVendorRecaps();
    },
    previous() {
      if (this.page > 1) {
        this.page -= 1;
        this.getVendorRecaps();
      }
    },
    next() {
      if (!this.disableNext) {
        this.page += 1;
        this.getVendorRecaps();
      }
    },
  },
};
</script>
